<template>
  <div class="user-card-container">
    <template v-if="user">
      <!-- Header moderno -->
      <div class="user-header">
        <div class="header-content">
          <h4 class="user-title">
            <b-icon-person class="title-icon"></b-icon-person>
            {{ user.first_name }} {{ user.last_name }}
          </h4>
          <p class="user-subtitle">
            {{ $t("reusable.userCard.userConfigAndData") }}
          </p>
        </div>
      </div>

      <b-card class="user-card" no-body>
        <!-- Sección de imagen -->
        <div class="image-section">
          <div class="image-container">
            <div class="image-wrapper">
              <template v-if="user && user.user_data && user.user_data.image">
                <ImageEditor
                  :key="user_image_key"
                  :display_options="false"
                  :deleted_botton="true"
                  :portrait_size="true"
                  :image_id="user.user_data.image"
                  @deleted="slotImageDeleted"
                  class="user-image"
                ></ImageEditor>
              </template>
              <template v-else>
                <div class="default-image">
                  <b-icon-person class="default-icon"></b-icon-person>
                </div>
              </template>
            </div>
            <div class="buttons-container">
              <ButtonMedium
                v-if="is_owner"
                :tooltip_text="''"
                :text_button="
                  user && user.user_data && user.user_data.image
                    ? $t('reusable.userCard.changeProfilePhoto')
                    : $t('reusable.userCard.selectProfilePhoto')
                "
                :click_button="() => $bvModal.show('modal-select-image')"
                :icon="''"
                :variant="'primary'"
                :custom_class="'image-button'"
                :disabled_button="false"
              >
              </ButtonMedium>
              <ButtonMedium
                :tooltip_text="''"
                :text_button="$t('reusable.userCard.updatePassword')"
                :click_button="
                  () => $bvModal.show(`modal-update-password-${user.id}`)
                "
                :icon="''"
                :variant="'secondary'"
                :custom_class="'password-button'"
                :disabled_button="false"
              >
              </ButtonMedium>
            </div>
          </div>
        </div>

        <!-- Sección de información del usuario -->
        <div class="config-section">
          <div class="config-content">
            <!-- Información Personal -->
            <div class="config-group">
              <div class="group-header">
                <h5 class="group-title">
                  <b-icon-person-badge class="group-icon"></b-icon-person-badge>
                  {{ $t("reusable.userCard.personalInformation") }}
                </h5>
                <div class="group-subtitle">
                  {{ $t("reusable.userCard.personalInformationSubtitle") }}
                </div>
              </div>
              <div class="fields-grid">
                <b-form-group class="modern-form-group">
                  <label class="modern-label">
                    <span class="label-text">{{
                      $t("reusable.userCard.lastName")
                    }}</span>
                  </label>
                  <b-form-input
                    v-if="allows_crud"
                    v-model="user.last_name"
                    class="modern-input"
                    :placeholder="$t('reusable.userCard.lastNamePlaceholder')"
                  ></b-form-input>
                  <div v-else class="static-value">
                    {{ user.last_name ? user.last_name : "-" }}
                  </div>
                </b-form-group>

                <b-form-group class="modern-form-group">
                  <label class="modern-label">
                    <span class="label-text">{{
                      $t("reusable.userCard.firstName")
                    }}</span>
                  </label>
                  <b-form-input
                    v-if="allows_crud"
                    v-model="user.first_name"
                    class="modern-input"
                    :placeholder="$t('reusable.userCard.firstNamePlaceholder')"
                  ></b-form-input>
                  <div v-else class="static-value">
                    {{ user.first_name ? user.first_name : "-" }}
                  </div>
                </b-form-group>

                <template v-if="user.user_data && identifier_types.length > 0">
                  <b-form-group class="modern-form-group">
                    <label class="modern-label">
                      <span class="label-text">{{
                        $t("reusable.userCard.identifierType")
                      }}</span>
                    </label>
                    <b-form-select
                      v-if="allows_crud"
                      :options="identifier_types"
                      value-field="id"
                      text-field="identifier_name"
                      v-model="user.user_data.identifier_type"
                      class="modern-input"
                    ></b-form-select>
                    <div v-else class="static-value">
                      {{
                        user.user_data.identifier_type
                          ? identifier_types.find(
                              (t) => t.id === user.user_data.identifier_type
                            )?.identifier_name
                          : "-"
                      }}
                    </div>
                  </b-form-group>

                  <b-form-group class="modern-form-group">
                    <label class="modern-label">
                      <span class="label-text">{{
                        $t("reusable.userCard.identifier")
                      }}</span>
                    </label>
                    <b-form-input
                      v-if="allows_crud"
                      v-model="user.user_data.identifier"
                      class="modern-input"
                      :placeholder="
                        $t('reusable.userCard.identifierPlaceholder')
                      "
                    ></b-form-input>
                    <div v-else class="static-value">
                      {{ user.user_data.identifier || "-" }}
                    </div>
                  </b-form-group>

                  <b-form-group class="modern-form-group">
                    <label class="modern-label">
                      <span class="label-text">{{
                        $t("reusable.userCard.gender")
                      }}</span>
                    </label>
                    <b-form-select
                      v-if="allows_crud"
                      v-model="user.gender"
                      :options="genders"
                      value-field="id"
                      text-field="value"
                      class="modern-input"
                    ></b-form-select>
                    <div v-else class="static-value">
                      {{
                        user.gender !== null && user.gender !== undefined
                          ? genders.find((g) => g.id === user.gender)?.value
                          : "-"
                      }}
                    </div>
                  </b-form-group>

                  <b-form-group class="modern-form-group">
                    <label class="modern-label">
                      <span class="label-text">{{
                        $t("reusable.userCard.birthDate")
                      }}</span>
                    </label>
                    <b-form-datepicker
                      v-if="allows_crud"
                      v-model="user.birth_date"
                      :placeholder="$t('reusable.userCard.selectDate')"
                      class="modern-input"
                    ></b-form-datepicker>
                    <div v-else class="static-value">
                      {{ user.birth_date || "-" }}
                    </div>
                  </b-form-group>
                </template>
              </div>
            </div>

            <!-- Información de Contacto -->
            <div class="config-group">
              <div class="group-header">
                <h5 class="group-title">
                  <b-icon-envelope class="group-icon"></b-icon-envelope>
                  {{ $t("reusable.userCard.contactInformation") }}
                </h5>
                <div class="group-subtitle">
                  {{ $t("reusable.userCard.contactInformationSubtitle") }}
                </div>
              </div>
              <div class="fields-grid">
                <b-form-group class="modern-form-group">
                  <label class="modern-label">
                    <span class="label-text">{{
                      $t("reusable.userCard.email")
                    }}</span>
                  </label>
                  <b-form-input
                    v-if="allows_crud"
                    v-model="user.email"
                    type="email"
                    class="modern-input"
                    :placeholder="$t('reusable.userCard.emailPlaceholder')"
                  ></b-form-input>
                  <div v-else class="static-value">{{ user.email || "-" }}</div>
                </b-form-group>
              </div>
            </div>

            <!-- TODO: Se oculta seccion de Idioma ya que ahora tenemos selector en navbar
            PD: Si se vuelve a activar recordar que el selector y esta seccion deben tener
            misma sincronia cuando se cambie el lenguaje -->
            <!-- Preferencias de Idioma -->
            <!-- <div v-if="languages.length > 1" class="config-group">
              <div class="group-header">
                <h5 class="group-title">
                  <b-icon-globe class="group-icon"></b-icon-globe>
                  {{ $t("reusable.userCard.platformLanguage") }}
                </h5>
                <div class="group-subtitle">
                  {{ $t("reusable.userCard.platformLanguageSubtitle") }}
                </div>
              </div>
              <div class="language-selector">
                <div
                  v-for="language in languages"
                  :key="language.id"
                  class="language-option"
                  :class="{ active: currentLanguage === language.code }"
                  @click="changeLanguage(language.code)"
                >
                  <div class="language-info">
                    <div class="language-name">
                      <img
                        v-if="language.code === 'es'"
                        src="@/assets/es_icon.png"
                        alt="Español"
                        class="flag-icon mr-2"
                      />
                      <img
                        v-else-if="language.code === 'en'"
                        src="@/assets/us_icon.png"
                        alt="English"
                        class="flag-icon mr-2"
                      />
                      {{ language.name }}
                    </div>
                  </div>
                </div>
              </div>
            </div> -->

            <!-- Sección de guardado -->
            <div class="save-section" v-if="allows_crud">
              <ButtonMedium
                :tooltip_text="''"
                :text_button="$t('reusable.userCard.saveChanges')"
                :click_button="save"
                :icon="''"
                :variant="'primary'"
                :custom_class="''"
                :disabled_button="false"
              >
              </ButtonMedium>
            </div>
          </div>
        </div>
      </b-card>

      <!-- Modal de cambio de contraseña -->
      <b-modal
        :id="`modal-update-password-${user.id}`"
        :title="$t('reusable.userCard.updatePasswordTitle')"
        size="lg"
        hide-footer
      >
        <div class="password-form-section">
          <b-form-group
            class="m-0"
            label-cols="0"
            label-cols-sm="3"
            :label="$t('reusable.userCard.currentPassword')"
          >
            <div class="d-flex align-center">
              <b-form-input
                v-model="$v.current_password.$model"
                :state="validateCurrentPassword('current_password')"
                :type="field_type1"
                autocomplete="off"
                aria-describedby="current-password-feedback"
                :placeholder="
                  $t('reusable.userCard.currentPasswordPlaceholder')
                "
                class="m-0"
              ></b-form-input>
              <b-button
                class="password-visibility ml-2"
                @click="currentPassword"
              >
                <b-icon v-if="current_password_visibility" icon="eye"></b-icon>
                <b-icon v-else icon="eye-slash"></b-icon>
              </b-button>
            </div>
            <b-form-invalid-feedback id="current-password-feedback">
              {{ $t("reusable.userCard.currentPasswordRequired") }}
            </b-form-invalid-feedback>
          </b-form-group>

          <b-form-group
            class="m-0"
            :label="$t('reusable.userCard.newPassword')"
            label-cols="0"
            label-cols-sm="3"
          >
            <div class="d-flex align-center">
              <b-form-input
                v-model="$v.new_password.$model"
                :type="field_type2"
                :state="validateNewPassword()"
                :placeholder="$t('reusable.userCard.newPasswordPlaceholder')"
                v-b-tooltip.v-secondary.noninteractive="
                  $t('reusable.userCard.newPasswordTooltip')
                "
                class="m-0"
              ></b-form-input>
              <b-button class="password-visibility ml-2" @click="newPassword">
                <b-icon v-if="new_password_visibility" icon="eye"></b-icon>
                <b-icon v-else icon="eye-slash"></b-icon>
              </b-button>
            </div>
          </b-form-group>

          <b-form-group
            class=""
            :label="$t('reusable.userCard.confirmPassword')"
            label-cols="0"
            label-cols-sm="3"
          >
            <div class="d-flex align-center">
              <b-form-input
                v-model="$v.confirm_password.$model"
                :state="validateConfirmPassword()"
                :type="field_type3"
                :placeholder="
                  $t('reusable.userCard.confirmPasswordPlaceholder')
                "
                class="m-0"
              ></b-form-input>
              <b-button
                class="password-visibility ml-2"
                @click="confirmPassword"
              >
                <b-icon v-if="confirm_password_visibility" icon="eye"></b-icon>
                <b-icon v-else icon="eye-slash"></b-icon>
              </b-button>
            </div>
            <div
              class="password-mismatch-warning"
              v-if="new_password != confirm_password && confirm_password != ''"
            >
              <b-icon icon="exclamation-circle"></b-icon>
              {{ $t("reusable.userCard.passwordMismatch") }}
            </div>
          </b-form-group>

          <div class="text-right">
            <ButtonMedium
              :tooltip_text="''"
              :text_button="$t('reusable.userCard.savePassword')"
              :click_button="savePassword"
              :icon="''"
              :variant="'primary'"
              :custom_class="''"
              :disabled_button="false"
            >
            </ButtonMedium>
          </div>
        </div>
      </b-modal>
    </template>

    <!-- Modal de selección de imagen -->
    <b-modal
      id="modal-select-image"
      :title="$t('reusable.userCard.selectImageTitle')"
      hide-footer
      size="md"
    >
      <ImageSelector @created="slotImageCreated"></ImageSelector>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import * as names from "@/store/names";
import { generateUniqueId, toast } from "@/utils/utils";
import { validationMixin } from "vuelidate";
import {
  required,
  minValue,
  maxValue,
  sameAs,
  minLength,
} from "vuelidate/lib/validators";

export default {
  name: "UserCard",
  mixins: [validationMixin],
  props: {
    User: {
      type: Object,
      required: true,
    },
  },
  components: {
    ImageEditor: () => import("@/components/reusable/ImageEditor"),
    ImageSelector: () => import("@/components/reusable/ImageSelector"),
    ButtonMedium: () =>
      import("@/components/reusable/Buttons/Medium/ButtonMedium"),
  },
  data() {
    return {
      user: this.User,
      student_card: {
        id: generateUniqueId(),
        user: this.User.id,
        cohort: null,
        high_school_grade: null,
        psu_average: null,
        psu_year: null,
        campus: null,
        career: null,
        egress_profile: null,
        faculty: null,
        shift: null,
        admission_profile: null,
        admission_profile_finished: false,
      },
      genders: [],
      admission_profiles: [],
      allows_crud: false,
      is_owner: false,
      identifier_types: [],
      current_password: "",
      new_password: "",
      confirm_password: "",
      current_password_visibility: false,
      new_password_visibility: false,
      confirm_password_visibility: false,
      field_type1: "password",
      field_type2: "password",
      field_type3: "password",
      user_image_key: 0,
      currentLanguage: this.$i18n.locale,
      languages: [],
    };
  },
  validations() {
    return {
      student_card: {
        cohort: { minValue: minValue(1990), maxValue: maxValue(2100) },
        psu_year: { minValue: minValue(1990), maxValue: maxValue(2100) },
      },
      current_password: { required },
      new_password: {
        required,
        minLength: minLength(8),
        containsUppercase: function (value) {
          return /[A-Z]/.test(value);
        },
        containsLowercase: function (value) {
          return /[a-z]/.test(value);
        },
        containsNumber: function (value) {
          return /[0-9]/.test(value);
        },
        containsSpecial: function (value) {
          return /[.#?!@$%^&*-]/.test(value);
        },
      },
      confirm_password: { required, sameAsPassword: sameAs("new_password") },
    };
  },
  computed: {
    ...mapGetters({
      campuses: names.CAMPUSES,
      faculties: names.FACULTIES,
      careers: names.CAREERS,
      egressProfiles: names.EGRESS_PROFILES,
      shifts: names.SHIFTS,
      loged_user: "getUser",
    }),
    campus() {
      const campus = this.campuses.find(
        (x) => x.id == this.student_card.campus
      );
      return campus ? campus.name : "-";
    },
    faculty() {
      const faculty = this.faculties.find(
        (x) => x.id == this.student_card.faculty
      );
      return faculty ? faculty.name : "-";
    },
    filteredFaculties() {
      let list = [];
      if (this.student_card.campus) {
        this.faculties.forEach((faculty) => {
          if (faculty.campuses.find((x) => x == this.student_card.campus))
            list.push(faculty);
        });
        return list;
      }
      return this.faculties;
    },
    career() {
      const career = this.careers.find((x) => x.id == this.student_card.career);
      return career ? career.name : "-";
    },
    filteredCareers() {
      if (this.student_card.faculty)
        return this.careers.filter(
          (x) => x.faculty == this.student_card.faculty
        );
      return this.careers;
    },
    egressProfile() {
      const egress_profile = this.egressProfiles.find(
        (x) => x.id == this.student_card.egress_profile
      );
      return egress_profile ? egress_profile.name : "-";
    },
    filteredEgressProfiles() {
      if (this.student_card.career)
        return this.egressProfiles.filter(
          (x) => x.career == this.student_card.career
        );
      return this.egressProfiles;
    },
    shift() {
      const shift = this.shifts.find((x) => x.id == this.student_card.shift);
      return shift ? shift.name : "-";
    },
    admissionProfile() {
      if (this.student_card.admission_profile) {
        const admission_profile = this.admission_profiles.find(
          (x) => x.id == this.student_card.admission_profile
        );
        if (admission_profile) return admission_profile;
      }
      return null;
    },
    admissionProfileName() {
      if (this.admissionProfile) return this.admissionProfile.name;
      return "-";
    },
  },
  methods: {
    validateNewPassword() {
      const { $dirty, $error } = this.$v.new_password;
      return $dirty ? !$error : null;
    },
    validateConfirmPassword() {
      const { $dirty, $error } = this.$v.confirm_password;
      return $dirty ? !$error : null;
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.student_card[name];
      return $dirty ? !$error : null;
    },
    validateCurrentPassword() {
      const { $dirty, $error } = this.$v.current_password;
      return $dirty ? !$error : null;
    },
    save() {
      if (this.user.user_data) {
        const item = {
          user: this.user.id,
          identifier_type: this.user.user_data.identifier_type,
          identifier: this.user.user_data.identifier,
          image: this.user.user_data.image,
        };
        this.saveUserData(item);
      }
      this.$v.student_card.$touch();
      if (this.$v.student_card.$anyError) {
        return;
      }
      if (isNaN(this.student_card.id)) this.createStudentCard();
      else this.updateStudentCard();
    },
    createStudentCard() {
      this.$restful
        .Post(`/cideu/student-card/`, this.student_card)
        .then((response) => {
          this.student_card = response;
          toast(this.$t("reusable.userCard.profileCreated"));
        });
    },
    updateStudentCard() {
      this.$restful
        .Put(`/cideu/student-card/${this.student_card.id}/`, this.student_card)
        .then((response) => {
          this.student_card = response;
          toast(this.$t("reusable.userCard.profileUpdated"));
        });
    },
    slotImageCreated(image) {
      let new_user_data = null;
      let item = null;
      if (this.user.user_data) {
        new_user_data = this.user.user_data;
        if (image) {
          new_user_data.image = image.id;
          item = {
            image: new_user_data.image,
          };
        }
      } else {
        if (image) {
          new_user_data = {
            image: image.id,
            user: this.user.id,
          };
          item = {
            image: new_user_data.image,
            user: new_user_data.user,
            identifier_type: null,
          };
        }
      }
      // Se quita condición correspondiente a estructura antigua de user (ahora se cambia desde user-data)
      // if (this.user.user_data.image) {
      //   try {
      //     const image_id = this.user.user_data.image;
      //     this.patchUserData(item);
      //     this.$store.dispatch(names.DELETE_IMAGE, image_id);
      //   } catch (error) {
      //     this.patchUserData(item);
      //   }
      // }
      // else {
      //   console.log(item);
      // }
      if (item) {
        this.saveUserData(item);
      }
    },
    saveUserData(item) {
      this.user_image_key += 1;
      const payload = {
        item: item,
      };
      if (this.user.user_data) {
        this.$restful
          .Patch(
            `/authentication/user-data/${this.user.user_data.id}/`,
            payload.item
          )
          .then((response) => {
            this.user.user_data.image = response.image;
            this.$store.commit("setUser", this.user);
          });
      } else {
        this.$restful
          .Post(`/authentication/user-data/`, payload.item)
          .then((response) => {
            this.user.user_data = response;
            this.$store.commit("setUser", this.user);
          });
      }
      // this.$store.dispatch(names.PATCH_USER, payload).then((response) => {
      //   this.$store.commit('setUser', response);
      //   if (response) this.user = response;
      //   toast("Usuario actualizado.");
      // });
      this.$bvModal.hide("modal-select-image");
    },
    slotImageDeleted() {
      const item = {
        image: null,
      };
      this.$restful
        .Patch(`/authentication/user-data/${this.user.user_data.id}/`, item)
        .then((response) => {
          this.user.user_data.image = response.image;
          this.$store.commit("setUser", this.user);
        });
    },
    fetchStudentCard() {
      this.$restful
        .GetAll(`/cideu/student-card/?user=${this.user.id}`)
        .then((response) => {
          if (response.length > 0) {
            this.student_card = response[0];
            if (this.student_card.campus)
              this.$store.dispatch(
                names.FETCH_CAMPUSES,
                this.student_card.campus
              );
            if (this.student_card.faculty)
              this.$store.dispatch(
                names.FETCH_FACULTY,
                this.student_card.faculty
              );
            if (this.student_card.career)
              this.$store.dispatch(
                names.FETCH_CAREER,
                this.student_card.career
              );
            if (this.student_card.egress_profile)
              this.$store.dispatch(
                names.FETCH_EGRESS_PROFILE,
                this.student_card.egress_profile
              );
            if (this.student_card.shift)
              this.$store.dispatch(names.FETCH_SHIFT, this.student_card.shift);
          }
        });
    },
    fetchAdmissionProfiles() {
      const institution_id = this.$store.getters.getInstitutionId;
      if (!institution_id) return;
      this.$restful
        .GetAll(`/cideu/admission-profile/?institution=${institution_id}`)
        .then((response) => {
          this.admission_profiles = response;
        });
    },
    currentPassword() {
      this.current_password_visibility = !this.current_password_visibility;
      if (this.current_password_visibility) {
        this.field_type1 = "text";
      } else {
        this.field_type1 = "password";
      }
    },
    newPassword() {
      this.new_password_visibility = !this.new_password_visibility;
      if (this.new_password_visibility) {
        this.field_type2 = "text";
      } else {
        this.field_type2 = "password";
      }
    },
    confirmPassword() {
      this.confirm_password_visibility = !this.confirm_password_visibility;
      if (this.confirm_password_visibility) {
        this.field_type3 = "text";
      } else {
        this.field_type3 = "password";
      }
    },
    savePassword() {
      this.$v.new_password.$touch();
      this.$v.confirm_password.$touch();
      this.$v.current_password.$touch();
      if (
        this.$v.new_password.$anyError ||
        this.$v.confirm_password.$anyError ||
        this.$v.current_password.$anyError
      ) {
        return;
      }
      let payload = {
        old_password: this.current_password,
        new_password: this.new_password,
        repeated_password: this.confirm_password,
      };
      this.$restful
        .Put(`/authentication/password/`, payload)
        .then((response) => {
          if (response) {
            this.$bvModal.hide(`modal-update-password-${this.user.id}`);
            setTimeout(() => {
              toast(this.$t("reusable.userCard.passwordUpdatedSuccess"));
            }, 300);
          }
        })
        .catch(() => {
          return this.$swal({
            text: this.$t("reusable.userCard.incorrectPassword"),
            type: "warning",
          });
        });
    },
    changeLanguage(lang) {
      this.currentLanguage = lang;
      this.$i18n.locale = lang;
      localStorage.setItem("user_language", lang);
      this.$store.commit(names.SET_USER_LANGUAGE, lang);

      // Buscar el ID del idioma seleccionado
      const selectedLanguage = this.languages.find((l) => l.code === lang);
      const languageId = selectedLanguage ? selectedLanguage.id : null;

      // Actualizar el default_language del user_data en el backend
      if (languageId) {
        const reloadAlternativeNames = () => {
          localStorage.removeItem("alternative_names");
          this.$store.commit(names.MUTATE_ALTERNATIVE_NAMES, []);
          this.$store
            .dispatch(names.FETCH_ALTERNATIVE_NAMES, true)
            .then((alternative_names) => {
              localStorage.setItem(
                "alternative_names",
                JSON.stringify(alternative_names)
              );
            });
        };

        if (this.user.user_data) {
          this.$restful
            .Patch(`/authentication/user-data/${this.user.user_data.id}/`, {
              default_language: languageId,
            })
            .then(() => {
              this.user.user_data.default_language = languageId;
              reloadAlternativeNames();
            });
        } else {
          this.$restful
            .Post(`/authentication/user-data/`, {
              user: this.user.id,
              default_language: languageId,
            })
            .then((response) => {
              this.user.user_data = response;
              this.$store.commit("setUser", this.user);
              reloadAlternativeNames();
            });
        }
      }

      toast(
        lang === "es"
          ? this.$t("reusable.userCard.languageChangedToSpanish")
          : this.$t("reusable.userCard.languageChangedToEnglish")
      );
    },
  },
  created() {
    // Inicializar géneros con traducciones
    this.genders = [
      { id: 0, value: this.$t("reusable.userCard.male") },
      { id: 1, value: this.$t("reusable.userCard.female") },
      { id: 2, value: this.$t("reusable.userCard.other") },
    ];

    if (this.loged_user.is_superuser) {
      // this.allows_crud = true;
      this.$store.dispatch(names.FETCH_CAMPUSES);
      this.$store.dispatch(names.FETCH_CAREERS);
      this.$store.dispatch(names.FETCH_EGRESS_PROFILES);
      this.$store.dispatch(names.FETCH_FACULTIES);
      this.$store.dispatch(names.FETCH_SHIFTS);
    }
    if (this.loged_user.id == this.user.id) this.is_owner = true;
    this.$restful.GetAll(`/common/identifer-type/`).then((response) => {
      this.identifier_types = response;
    });
    // Se oculta peticion ya que el modulo está comentado
    // this.$restful.GetAll("/common/language/").then((response) => {
    //   this.languages = response.filter((lang) => lang.is_active);
    // });
    this.fetchStudentCard();
    this.fetchAdmissionProfiles();
  },
};
</script>

<style scoped>
.flag-icon {
  width: 25px;
  height: auto;
  object-fit: contain;
  border-radius: 3px;
}
.user-card-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
}

.user-header {
  background: linear-gradient(
    135deg,
    var(--kl-color-accent-medium) 0%,
    var(--kl-color-primary) 100%
  );
  border-radius: var(--kl-radius-lg) var(--kl-radius-lg) 0 0;
  padding: 2rem;
  color: var(--kl-text-on-primary);
  margin-bottom: 0;
}

.header-content {
  text-align: center;
}

.user-title {
  font-family: var(--kl-font-family);
  font-size: 1.8rem;
  font-weight: var(--kl-fw-bold);
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.title-icon {
  font-size: 2rem;
}

.user-subtitle {
  margin: 0.5rem 0 0 0;
  opacity: 0.9;
  font-size: var(--kl-fs-body-lg);
}

.user-card {
  border-radius: 0 0 var(--kl-radius-lg) var(--kl-radius-lg);
  box-shadow: var(--kl-shadow-lg);
  border: none;
  overflow: hidden;
}

.image-section {
  background: var(--kl-surface-muted);
  padding: 2rem;
  border-bottom: 1px solid var(--kl-border-soft);
}

.image-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.image-wrapper {
  width: 200px;
  height: 200px;
  border-radius: 16px;
  overflow: visible;
  box-shadow: var(--kl-shadow-md);
  background: var(--kl-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.default-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    var(--kl-border-soft) 0%,
    var(--kl-surface-muted) 100%
  );
}

.default-icon {
  font-size: 4rem;
  color: var(--kl-text-muted);
}

.user-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.buttons-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
}

.image-button,
.password-button {
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.image-button:hover,
.password-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.config-section {
  padding: 2rem;
}

.config-content {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

.config-group {
  background: var(--kl-surface-muted);
  border-radius: var(--kl-radius-lg);
  padding: 1.5rem;
  border-left: 4px solid var(--kl-color-primary);
}

.group-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--kl-border-soft);
}

.group-title {
  font-family: var(--kl-font-family);
  font-size: 1.2rem;
  font-weight: var(--kl-fw-semibold);
  margin: 0 0 0.5rem 0;
  color: var(--kl-text-body);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.group-icon {
  color: var(--kl-color-primary);
}

.group-subtitle {
  color: var(--kl-text-muted);
  font-size: var(--kl-fs-body);
  margin: 0;
}

.fields-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}

.modern-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: var(--kl-fw-semibold);
  color: var(--kl-text-body);
  margin-bottom: 0.5rem;
  font-size: var(--kl-fs-body);
}

.label-text {
  flex: 1;
}

.modern-input {
  border-radius: var(--kl-input-radius);
  border: 2px solid var(--kl-input-border);
  padding: 0.75rem 1rem;
  font-size: var(--kl-fs-body);
  transition: all 0.3s ease;
  background: var(--kl-surface);
}

.modern-input:focus {
  border-color: var(--kl-input-border-focus);
  box-shadow: var(--kl-shadow-focus);
  transform: translateY(-1px);
}

.modern-input:disabled {
  background-color: var(--kl-surface-muted);
  opacity: 0.8;
}

.static-value {
  padding: 0.75rem 1rem;
  background-color: var(--kl-surface-muted);
  border-radius: var(--kl-input-radius);
  border: 2px solid var(--kl-input-border);
  color: var(--kl-text-body);
  font-size: var(--kl-fs-body);
}

.save-section {
  padding: 2rem;
  border-top: 1px solid var(--kl-border-soft);
  background: var(--kl-surface-muted);
  text-align: center;
}

.password-form-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.password-visibility {
  height: 2.35em;
}

.password-mismatch-warning {
  color: var(--kl-color-danger);
  font-size: var(--kl-fs-small);
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.language-selector {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.language-option {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: var(--kl-surface);
  border: 2px solid var(--kl-border-soft);
  border-radius: var(--kl-radius-lg);
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 150px;
}

.language-option:hover {
  border-color: var(--kl-color-primary);
  transform: translateY(-2px);
  box-shadow: var(--kl-shadow-md);
}

.language-option.active {
  border-color: var(--kl-color-primary);
  background: var(--kl-surface-hover);
  box-shadow: var(--kl-shadow-md);
}

.language-flag {
  font-size: 2.5rem;
  line-height: 1;
}

.language-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.language-name {
  display: flex;
  font-weight: var(--kl-fw-semibold);
  font-size: var(--kl-fs-body-lg);
  color: var(--kl-text-body);
}

.language-code {
  font-size: var(--kl-fs-small);
  color: var(--kl-text-muted);
  font-weight: var(--kl-fw-medium);
}

@media (max-width: 768px) {
  .user-card-container {
    padding: 1rem;
  }

  .user-header {
    padding: 1.5rem 1rem;
  }

  .user-title {
    font-size: 1.5rem;
  }

  .fields-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .config-section {
    padding: 1.5rem;
  }

  .config-group {
    padding: 1rem;
  }

  .image-wrapper {
    width: 150px;
    height: 150px;
  }

  .buttons-container {
    flex-direction: column;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .user-header {
    padding: 1rem;
  }

  .user-title {
    font-size: 1.3rem;
    flex-direction: column;
    gap: 0.5rem;
  }

  .image-section {
    padding: 1.5rem 1rem;
  }

  .config-section {
    padding: 1rem;
  }

  .image-wrapper {
    width: 120px;
    height: 120px;
  }
}
</style>